<template>
  <div class="nightlife-guide-container">
    <div class="guide-content">
      <div class="row">
        <!-- Main Content Column -->
        <div class="col-lg-8 col-sm-12 ">
          <HeadImage :image="mainImage" title="Tulum Nightlife"
                     alt="tulum epic nights"
                     :clamp="true"
                     align="text-center"
                     caption="Smart & Up to Date Guide For Party Goers" color="#73854a"/>
          <!-- Introductory Paragraphs -->
          <section class="intro-text text-center">
            <p>
              The nightlife scene in Tulum is<strong> booming </strong>and the town has quickly become a
              <strong>must-visit</strong> destination for people who enjoy nature, <strong>electronic music</strong>,
              and spiritual awareness.
            </p>
            <section class="nightlife-feature-section">
              <div class="feature-card">
                <hr class="section-divider">
                <p class="feature-text">
                  Tulum's vibrant music scene consistently draws a remarkable caliber of international and local DJs.
                  Throughout the year, its
                  <router-link to="/tulum-clubs">clubs</router-link>,
                  <router-link to="/">parties</router-link>, and
                  <router-link to="/tulum-new-year-festival-2025">festivals</router-link>
                  serve as dynamic stages for leading global electronic music talent, establishing it as a premier music destination.
                </p>
                <hr class="section-divider">


                <img v-lazy="djImage" class="pull-right" height="200px" alt="DJ mixing">
                  <img v-lazy="jungleImage" height="50px" width="50px" style="border: 1px solid orange;border-radius: 500px" alt="Schedule Icon">
                <router-link to="/tulum-dj-schedule" class="section-header">

                EXPLORE THE DJ SCHEDULE </router-link>
                <br>

              </div>

            </section>
          </section>


          <!-- Individual Sections -->
          <section id="why-tulum" class="content-section card gradient-card text-center">
            <div class="card-content">
              <h2><span class="bullet">🔘</span> WHY TULUM?</h2>

              <p>
                <strong>Tulum is recognized for its beautiful scenery and cultural importance</strong>.
                Moreover, it's a prime location for those looking to unwind in nature and enjoy the night.
                <br>
                <br>
              </p>

              <ResponsiveImage :image="whyTulumImage" align="center" alt="Spiritual and natural party vibe in Tulum" />
              <hr>

              <p>
                Tulum's vibrant music scene consistently draws a remarkable caliber of international and local DJs. Throughout the year, its clubs, parties, and festivals serve as dynamic stages for leading global electronic music talent, establishing it as a premier destination.
              </p>

              <br>

              <blockquote class="highlight-quote">
                "Tulum's party scene offers a diverse range of electronic music styles, including all variations of
                house & melodic techno."
              </blockquote>
              <small style=" color: #aaa;">
                * Not a fan of dance music? Try Salsa Nights (Palma Central, Batey), Latin Music (Taboo, Ilios), or
                Hip-Hop (Confessions) instead.
              </small>
            </div>
          </section>

          <section id="where-to-go" class="content-section card gradient-card text-center">
            <h2><span class="bullet">🔘</span> WHERE THE VIBE UNFOLDS</h2>
            <p>Tulum's nightlife scene is spread across <strong>four distinct areas</strong>. The <a href="#beach-zone">Beach
              Zone</a> is the
              epicenter of the action, while <a href="#downtown">Downtown</a> offers a relaxed vibe with extended hours.
              <br>
              <a href="#la-veleta">La Veleta</a> is a
              burgeoning hub for diverse entertainment, and the <a href="#jungle">Jungle hosts legendary festivals and the famous jungle parties.</a></p>




            <hr class="section-divider">

            <br>

            <div class="venue-cards-grid">
              <section id="beach-zone">

                <div class="venue-card">
                  <ResponsiveImage :image="beachZoneImage" alt="Tulum Beach Zone club" />
                  <hr>
                  <h3>The Beach Zone</h3>

                  <div class="venue-card-text text-left">
                    <section class="nightlife-zone-info">
                      <p>
                        A mere 10-minute ride from Downtown, the <strong>Beach Zone</strong> (also
                        known as the Hotel Zone) stands as the undisputed <strong>epicenter of Tulum's nightlife</strong>.
                        <br>Home to the most iconic <strong>Tulum</strong> venues, offering a
                        lively
                        atmosphere that seamlessly <strong>transitions from beach days to intense party nights</strong>.
                      </p>

                      <p>
                        Tulum's Beach Zone stretches along a 10km single, leafy road, lined with hotels,
                        <router-link to="/tulum-day-beach-clubs">Beach Clubs</router-link>
                        , stores & restaurants.
                        While the Tulum ruins lie to the north, most of the vibrant clubs and accommodations extend
                        south towards Sian Ka'an.
                      </p>

                      <br>
                      <p><strong>Within this bustling stretch, you'll discover iconic clubs such as:</strong></p>

                      <ul>
                        <li><strong>
                          <router-link to="/tulum-clubs/papaya-playa-tulum">Papaya Playa Project</router-link>:</strong> Famous for its legendary Full Moon (Saturday) parties and a
                          unique, rustic-chic vibe.
                        </li>
                        <li><strong>
                          <router-link to="/tulum-clubs/vagalume-tulum">Vagalume</router-link>
                          &
                          <router-link to="/tulum-clubs/mia-beach-club-tulum">Mia Tulum:</router-link>
                        </strong> The go-to spots for world-class international DJ sets
                          directly on the sand.
                        </li>
                        <li><strong>
                          <router-link to="/tulum-clubs/vagalume-tulum">Bagatelle</router-link>
                          &
                          <router-link to="/tulum-clubs/vagalume-tulum">Gitano Jungle</router-link>:</strong> Sophisticated venues where a gourmet dinner
                          transforms into a high-energy dance party.
                        </li>
                        <li><strong>
                          <router-link to="/tulum-clubs/bonbonniere-tulum">Bonbonniere</router-link>:</strong> For an exclusive, intimate, and late-night clubbing experience
                          with top-tier talent.
                        </li>
                      </ul>
                      <p><strong>And festive restaurants with high-quality shows</strong></p>
                      <ul>

                        <li><strong>
                          <router-link to="/tulum-clubs/rosa-negra-tulum">Rosa Negra</router-link>
                          &
                          <router-link to="/tulum-clubs/taboo-tulum">Taboo</router-link>
                        </strong> Latin Cuisine with Bongo & Spark show.
                        </li>
                        <li><strong> <router-link to="/tulum-clubs/ilios-tulum">Ilios</router-link></strong>: Experience the acclaimed <strong>
                          <router-link to="/tulum-clubs/ilios-restaurant-tulum/event/greek-fire-dinner-ilios-restaurant-tulum-sat">Ilios Greek Night</router-link></strong>: where <strong>fiery performances</strong> meet the exhilarating tradition of <strong>smashing plates</strong>, all set to vibrant <strong>Latin rhythms</strong>. An <strong>immersive culinary and artistic journey</strong> that feels just like home.
                        </li>
                        <li><strong>Casa Jaguar</strong>, offers <strong>select, intimate parties</strong>, beautifully <strong>tucked away</strong> in the Beach Zone.
                        </li>

                      </ul>

                    </section>

                  </div>

                </div>
              </section>

              <section id="downtown">

                <div class="venue-card">
                  <ResponsiveImage :image="downtownImage" alt="Bar in Downtown Tulum" />
                  <div class="venue-card-text text-left">
                    <h3>Downtown</h3>
                    <p>In contrast, <strong>Downtown</strong> offers a relaxed, local vibe with diverse places to eat, drink, and dance, all with more accessible
                      prices.</p>

                    <ul>
                      <li>
                        <p><a href='https://www.instagram.com/bateytulum' target="_blank">
                          Batey</a>, the mojitos bar, is a classic! Live Music, Tacos and Mojitos!</p>

                      </li>
                      <li>
                        <p><a href='https://www.instagram.com/bestiario.tulum' target="_blank">Bestiario</a> is a great option for those
                          who
                          enjoy house/techno dancing</p>

                      </li>
                      <li>
                        <strong><a href="https://www.mistico.mx/" target="_blank">Mistico Downtown</a>, <a
                          href="https://www.facebook.com/SantinoTulum" target="_blank">Santino</a> & <a
                          href="https://www.instagram.com/strawhathostels/" target="_blank">Straw Hat</a></strong> are great places to
                        meet people, featuring reggaeton, rap,
                        trip-hop, and Latin music.

                      </li>
                    </ul>


                    <p>
                      <strong>PRO TIP:</strong> There are plenty of small parties going on inside hostels. (<strong>e.g.,</strong> Mayan Monkey, El Che,
                      Emotion)
                    </p>


                  </div>


                </div>

              </section>

              <section id="la-veleta">

                <div class="venue-card">
                  <ResponsiveImage :image="laVeletaImage" alt="Emerging scene in La Veleta, Tulum" />
                  <div class="venue-card-text">
                    <h3>La Veleta</h3>
                    <p>Further inland, <strong>La Veleta</strong> emerges as Tulum's burgeoning hub, offering a diverse range of new dining and entertainment options.
                      Here, you'll find restaurants open late near Kukulkan and Palenque avenues.
                    </p>

                  </div>
                </div>
              </section>
              <section id="jungle">
                <div class="venue-card">
                  <ResponsiveImage :image="jungleImage" alt="Jungle Party At Zamna Tulum" />
                  <div class="venue-card-text">
                    <h3>The Jungle</h3>

                    <p>
                      Immerse yourself in <strong>enchanting, unforgettable parties</strong> deep within Tulum's <strong>verdant jungle</strong>. This mystical setting hosts legendary festivals like <router-link to="/brands/day-zero-tulum">Day Zero</router-link> (at Cenote Dos Ojos)
                      and <router-link to="/brands/zamna-tulum">Zamna</router-link>, offering unique <strong>Jungle Parties</strong> and <strong>Cenote Parties</strong>.
                      Explore unique Cenote Clubs like <router-link to="/tulum-clubs/vesica-tulum">Vesica</router-link>, which regularly provide <strong>a stunning, immersive environment</strong> for a profound musical adventure.
                    </p>

                  </div>
                </div>
              </section>
            </div>
          </section>


          <section id="when-to-go" class="content-section">
            <h2><span class="bullet">🔘</span> WHEN TO DIVE IN</h2>
            <h4 class="section-header">Year Round Rhythm</h4>

            <p>
              Tulum's party scene is lively year-round, with more big festivals and parties during <strong>December,
              January & February</strong>.
            </p>
            <div class="card text-center" style="flex-direction: column; margin-top: 2rem; margin-bottom: 2rem;">
              <NewYearCarrousel></NewYearCarrousel>
            </div>
            <p>
              Consequently, thrill-seekers and partiers flock to Tulum mainly in the winter
              months looking for a warm place to have fun and usher in the new year.
              <br>This is <strong>the time when the big festivals and immense parties take place</strong>, so it's no
              surprise that every year this season becomes the <strong>party season</strong>.
            </p>
            <div class="well">
              If you've never come to Tulum, you should try it on
              <router-link to="/nye">New Year's Eve</router-link>
              , and if you've finally pulled the trigger, we want to make sure you know what to do here once you get
              here.
            </div>
            <br>

            <h4 class="section-header">Weekly Rhythm</h4>
            <p>
              Delving into the weekly rhythm, activity tends to start low on <strong>Monday</strong> but gradually picks up pace as the week
              progresses, <strong>reaching its peak on Friday and Saturday.</strong>
            </p>


            <br>


            <!-- ✅ UPDATED: Replaced table with a card grid -->
            <div class="card-grid">
              <div class="day-card">
                <hr class="section-divider">

                <ResponsiveImage :image="mondayImage" alt="Relaxed Monday night in Tulum" />
                <div class="day-card-content">
                  <h3>Monday</h3>
                  <p>On <strong>Monday</strong>, Tulum is relatively quiet. <strong>However, for a lively night,</strong> visit
                    <router-link to="/tulum-clubs/rosa-negra-tulum">Rosa Negra</router-link>
                    ,
                    <router-link to="/tulum-clubs/ilios-restaurant-tulum">Ilios</router-link>
                    , or
                    <router-link to="/tulum-clubs/taboo-tulum">Taboo Tulum</router-link>
                    .
                    La Pizzine Tulum offers a party ambiance perfect to heat up the week.
                    You can also explore bars and clubs in downtown Tulum like Batey Tulum or Santino.
                  </p>
                </div>

              </div>
              <div class="day-card">
                <hr class="section-divider">

                <ResponsiveImage :image="tuesdayImage" alt="Vibrant Tuesday party at Vagalume" />
                <div class="day-card-content">
                  <h3>Tuesday</h3>
                  <p>As the week progresses, <strong>Tuesdays</strong> see the party scene heat up. Beach clubs and bars host Resident <strong>DJs</strong> or
                    seasonal events like
                    <router-link to="/brands/sinner-tulum">Sinner</router-link>
                    at Vagalume.

                    <strong>In downtown</strong>, <a href="https://palmacentral.com/" target="_blank">Palma Central</a> has Salsa
                    Nights/Lessons and Food Trucks.

                  </p>
                </div>
              </div>
              <div class="day-card">
                <hr class="section-divider">
                <ResponsiveImage :image="wednesdayImage" alt="Ilios on a Wednesday" />
                <div class="day-card-content">
                  <h3>Wednesday</h3>
                  <p>
                    At
                    <router-link to="/tulum-clubs/ilios-restaurant-tulum">Ilios Tulum</router-link>
                    , <strong>experience</strong> a unique Greek Night infused with the warmth of Latin music, with captivating fire shows and
                    the joyful tradition of smashing plates.
                  </p>
                </div>
              </div>
              <div class="day-card">
                <hr class="section-divider">

                <ResponsiveImage :image="thursdayImage" alt="Dinner party at Bagatelle Tulum on a Thursday" />
                <div class="day-card-content">
                  <h3>Thursday</h3>
                  <p>By <strong>Thursday</strong>,
                    <router-link to="/tulum-clubs/bagatelle-tulum">Bagatelle</router-link>
                    is the hot spot for its enjoyable
                    <router-link to="/article/bagatelle-tulum-dinner-party">dinner + party sessions</router-link>
                    . Later,
                    <router-link to="/tulum-clubs/vagalume-tulum">Vagalume Tulum</router-link>
                    is a top pick for dancing on the beach.
                  </p>
                </div>
              </div>
              <div class="day-card">


                <hr class="section-divider">
                <router-link to="/tulum-clubs/gitano-tulum">
                  <ResponsiveImage :image="fridayImage" alt="Gypsy Disco at Gitano Jungle on a Friday" />
                </router-link>

                <div class="day-card-content">
                  <h3>Friday</h3>
                  <p><strong>Fridays</strong> offer a variety of options, starting early with cenote parties. In the
                    evening, head to <a href="/tulum-clubs/gitano-tulum" target="_blank">Gitano Jungle</a> for their signature Gypsy
                    Disco night, where you can dine and dance under the giant disco ball.</p>
                  <a href="/tulum-clubs/vagalume-tulum" target="_blank">Vagalume</a> and <a href="/tulum-clubs/bonbonniere-tulum" target="_blank">Bonbonniere</a>
                  are sure bets for <strong>Friday</strong> nights.
                </div>
              </div>
              <div class="day-card">
                <hr class="section-divider">

                <ResponsiveImage :image="saturdayImage" alt="Full Moon party at Papaya Playa Project" />
                <div class="day-card-content">
                  <h3>Saturday</h3>
                  <p>
                    Saturdays in Tulum explode with excitement. Most clubs unveil special events, ensuring a perfect match for every taste. By day,  <a href="/tulum-clubs/taboo-tulum" target="_blank">Taboo</a> commands attention with its captivating atmosphere. As night falls,  <a href="/tulum-clubs/papaya-playa" target="_blank">Papaya Playa Beach Club</a> draws crowds with its renowned weekly gatherings and <strong>legendary Full Moon parties</strong> (occurring <strong>monthly</strong>). Meanwhile, <router-link to="/tulum-clubs/bagatelle-tulum">Bagatelle</router-link> buzzes with groups eager to indulge in exquisite food and vibrant dance floors, while <router-link to="/tulum-clubs/vagalume-tulum">Vagalume</router-link> and <router-link to="/tulum-clubs/bonbonniere-tulum">Bonbonniere</router-link> await those seeking an elevated experience.

                  </p>
                </div>
              </div>
              <div class="day-card">
                <hr class="section-divider">

                <ResponsiveImage :image="sundayImage" alt="Full Moon party at Papaya Playa Project" />
                <div class="day-card-content">
                  <h3>Sunday</h3>
                  <p>
                    <strong>Sunday</strong> keeps the rhythm alive! Start your day with vibrant morning party vibes and brunches, like the <router-link to="/tulum-clubs/bagatelle-tulum/event/jungle-brunch-series-bagatelle-tulum-sun">Jungle Brunch at Bagatelle</router-link>. Or dive into Cenote Parties before the energy shifts to clubs like Vagalume. The week then culminates in a ritualistic closing party at Bonbonniere, offering the perfect final dance to burn off any lingering energy.
                  </p>

                </div>
              </div>
            </div>

          </section>

          <hr class="section-divider">
          <section>
            <h3 class="section-header">EXPLORE OUR INTERACTIVE MAP</h3>
            <router-link to="/tulum-clubs-map">
              <div class="brand-grid">
                <div class="card-image-container">
                  <ResponsiveImage :image="mapImage" alt="Tulum interactive map" class="brand-image" hs="100" ws="300" hl="100" wl="300" clamp/>
                  <div class="card-image-overlay">
                    <h4 class="upcoming-artist-event-section">TULUM NIGHTLIFE MAP</h4>
                    <p class="brand-legend">FIND YOUR VIBE</p>
                  </div>
                </div>
              </div>
            </router-link>
          </section>

          <section class="dos-donts-section">
            <h2 class="dos-donts-header">Insider's Guide to a Flawless Tulum Experience</h2>
            <div class="dos-donts">
              <div class="column card gradient-card-dos">
                <h3><span class="emoji">⚡</span> DO's</h3>
                <ul>
                  <li>
                    <strong>Plan Your Logistics:</strong> Tulum is unique. Transportation can be challenging, especially
                    in the high season when traffic in the Beach Zone is heavy. Plan ahead by staying near the venues
                    you want to visit most.
                  </li>
                  <li>
                    <strong>Embrace the Day:</strong> The magic of Tulum isn't just at night. Spend your days exploring
                    the stunning cenotes, ancient ruins, and beautiful beaches to get the full experience.
                  </li>
                  <li>
                    <strong>Tulum Should Not Be Expensive:</strong> Prices on the Beach Zone tend to be much higher than
                    prices on the Downtown area. Eat where <strong>locals</strong> eat (Street Tacos/ Downtown Clubs & Restaurants), Buy
                    where <strong>locals</strong> buy (Super Aki / Oxxo)
                  </li>
                  <li>
                    <strong>Use a Concierge Service:</strong> This is the ultimate pro tip. Concierge services are
                    typically free (as they are paid by the venues) and can handle all the details for you, from
                    personalized recommendations, securing reservations to <strong>providing</strong> assistance <strong>all the</strong> way through. It's the best way to enhance
                    your experience, especially if it's your first time.
                  </li>
                  <li>
                    <strong>Embrace the Full Riviera Maya Journey (10-Day Plan):</strong> To truly experience the magic,
                    give yourself at least 10 days. Start by soaking in the vibrant energy of <strong>Playa del Carmen</strong> for a
                    day or two. Next, immerse yourself in <strong>Tulum</strong> for 3 days of legendary parties and mystical
                    cenotes—don't miss the ancient ruins of Coba. Then, escape to the stunning "Lagoon of Seven Colors"
                    in <strong>Bacalar</strong> for 2-3 days of pure magic. Finish your adventure back in Playa with a celebratory
                    stroll down the famous <strong>5th Avenue</strong>.
                  </li>
                </ul>
              </div>
              <div class="column card gradient-card-donts">
                <h3><span class="emoji">⚠️</span> DONT'S</h3>
                <ul>
                  <li>
                    <strong>Don't Drink and Drive:</strong> This is non-negotiable. Taxis are readily available. Driving
                    under the influence has severe consequences.
                  </li>
                  <li>
                    <strong>Don't Underestimate Closing Times:</strong> Be aware that most clubs in the Beach Zone close
                    around 1-2 AM. If you're looking for a party that goes until sunrise, you'll need to head to one of
                    the special jungle parties or after-parties.
                  </li>
                  <li>
                    <strong>Don't Forget Standard Safety:</strong> Be respectful, be aware of your surroundings, and
                    never lie to the police. Stick to well-known tourist areas at night to ensure a secure and enjoyable
                    experience.
                  </li>

                </ul>
              </div>

              <hr class="section-divider">
              <section>

                <div class="text-center">
                  <a
                    href="https://wa.me/524421867547?text=Thanks%20for%20contacting%20us!%20How%20can%20we%20help%3F"
                    v-on:click="onQuickReserveClick('Party Support')"
                    class="btn btn-sm btn-primary font-weight-bold d-inline-xs"
                    style="
                      width: 80%;

                      margin-left: auto;
                      margin-right: auto;
                      left: 0;
                      right: 0;
                      padding: 10px;
                      border: 1px solid silver;
                    "
                  >
                    📲 INFO & RESERVATIONS
                  </a>

                  <br />
                  <br />

                  <br />
                  <a href="mailto:info@tulum.party">info@tulum.party</a>

                  <br />

                  <small>
                    We operate from 9AM - 10PM / Answer within 15 mins</small
                  >
                  <br />
                  <br />
                  <small style="color: #fff; font-weight: bolder"
                  >NO EXTRA FEE</small
                  >
                </div>
              </section>


              <hr class="section-divider">
              <div
                class="card"
                style="
                      z-index: 1;
                      text-align: center;
                      max-width: 80%;
                      margin-left: auto;
                      margin-right: auto;
                      left: 0;
                      right: 0;
                    "
              >
                <p style="font-weight: bold">
                  “Whenever you find yourself on the side of the majority,
                  it is time to pause and reflect.” -Mark Twain.
                </p>

                <a href="https://www.instagram.com/tulum.party/" style="padding: 30px" target="_blank">
                  &nbsp;
                  <img v-lazy="insta" height="30px" width="30px" alt="follow tulum party in instagram"/>&nbsp;Follow Us
                </a>
              </div>
            </div>
          </section>
        </div>

        <!-- Sidebar Column -->
        <aside class="card col-4 col-lg-4 d-none d-lg-inline">
          <SmartMenu :links="links" :logo="logo" :primaryColor="primaryColor">
            <div class="row">
              <div class="col-sm-12 text-center">
                <p><strong>Feeling Lost?</strong></p>
                <hr>
                <p>
                  We know the scene inside out. Let's curate your perfect night.
                </p>
                <hr>
              </div>
            </div>
            <template v-slot:section1>
              <section
                class="col-sm-12"
                style="margin: 10px;"
              >
                <h4 class="section-header">
                  <i class="tim-icons icon-settings"></i>Clubs
                </h4>
                <div class="row">
                  <div class="col-sm-12" style="min-height: 500px">
                    <ul v-for="club in activeClubsList" class="text-left">
                      <li v-if="club.slug">
                        <router-link :to="club.slug">
                          {{ club.name }}
                        </router-link>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </template>
          </SmartMenu>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
// Assuming these components are globally registered or imported
import SmartMenu from '@/components/SmartMenu/SmartMenu';
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';
import MiniCarrousel from '@/components/MiniCarrousel'; // Not used in template, consider removing.
import NewYearCarrousel from '@/components/NewYearCarrousel';
import HeadImage from '@/components/HeadImage/HeadImage';
import clubsConfig from '@/common/clubsConfig';
import ImgCard from '@/components/Cards/ImgCard'; // Not used in template, consider removing.
import Carrousel from '@/components/Carrousel'; // Not used in template, consider removing.
import DynamicLinks from '@/components/DynamicLinks'; // Not used in template, consider removing.


export default {
  name: 'TulumNightLife',
  components: {
    HeadImage,
    NewYearCarrousel,
    ResponsiveImage,
    SmartMenu,
    // Removed unused components: MiniCarrousel, ImgCard, Carrousel, DynamicLinks
  },
  created: function() {
    this.clubsConfig = Object.fromEntries(Object.entries(clubsConfig).filter(([clubId, clubData]) => {
      return !clubData || clubData.closed !== true;
    }));
    this.$store.dispatch('getLinks', { slug: 'tulum-nightlife' });
  },
  computed: {
    links: {
      get() {
        return (this.$store.state.links || []);
      }
    },
    activeClubsList() {
      return Object.entries(this.clubsConfig)
        .map(([id, clubData]) => ({ id, ...clubData }))
        .filter(club => !club.closed);
    }
  },
  props: ['slug'],
  metaInfo() {
    return {
      title: `Tulum Nightlife Guide 2025 | Best Parties, Clubs & Raves`,
      meta: [
        {
          name: 'description',
          content: `Your ultimate guide to Tulum's nightlife. Discover the best beach clubs, jungle raves, and downtown bars. Get insider tips for a world-class party experience.`
        },
        { property: 'og:title', content: `Tulum Nightlife: Your Guide to a Bohemian Party Paradise` },
        {
          property: 'og:description',
          content: `Unlock Tulum's legendary nights with our expert guide to the best clubs, jungle parties, and beach events.`
        },
        { property: 'og:image', content: this.mainImage },
        { property: 'og:url', content: `https://tulum.party/tulum-nightlife` }
      ]
    };
  },
  data: () => {
    return {
      logo: 'https://imgix.cosmicjs.com/07af7270-5002-11ef-b1ea-f56c65dfade9-tulum-party-insta.jpg',
      secondaryColor: '#73854a',
      primaryColor: '#5a7898',
      djImage: 'https://imgix.cosmicjs.com/9f39a6a0-c70a-11ee-9ce5-59949019255e-dj.png',
      insta:
        'https://imgix.cosmicjs.com/6d099b90-5c09-11ec-a8a3-53f360c99be6-instagram.png',
      mapImage: 'https://imgix.cosmicjs.com/25407510-7265-11f0-8548-558132fa66ae-Interactive_Map_of_Tulum_Clubs___Nightlife.jpg',
      mainImage: 'https://imgix.cosmicjs.com/e1e6a770-dc9d-11ee-b074-b5c8fe3ef189-nightlife-tulum.png',
      whyTulumImage: 'https://imgix.cosmicjs.com/b3790c40-73a8-11f0-b9ca-7b34664f6142-jungle-party-zamna.jpg',
      beachZoneImage: 'https://imgix.cosmicjs.com/6fe27ad0-72c6-11f0-8548-558132fa66ae-beach-zone.jpg',
      downtownImage: 'https://imgix.cosmicjs.com/189f3230-72c7-11f0-8548-558132fa66ae-Batey_Mojito_and_Guarapo_Bar_-_Google_Maps.jpg',
      jungleImage: 'https://imgix.cosmicjs.com/e84ef750-d82a-11ef-bee4-3bb1d3c55332-zamna-tulum-2025-3.jpg',
      laVeletaImage: 'https://imgix.cosmicjs.com/f4f43a30-7189-11f0-a97c-19f0968aeae2-la-veleta.jpg',
      beachClubsImage: 'https://imgix.cosmicjs.com/3e6b9220-d4cf-11ee-9ce5-59949019255e-vagalume-10.jpg', // Not used in template, consider removing.
      junglePartiesImage: 'https://imgix.cosmicjs.com/5a39f560-c8f3-11ee-9ce5-59949019255e-day-zero-main-stage.jp', // Not used in template, consider removing.
      downtownBarsImage: 'https://imgix.cosmicjs.com/72c50be0-72c7-11f0-8548-558132fa66ae-RosaNegra___Latin_American_Restaurant_in_Tulum.jpg', // Not used in template, consider removing.
      // Placeholders for weekly rhythm
      mondayImage: 'https://imgix.cosmicjs.com/942db250-63c3-11ef-b5ae-a594bb4a8e67-imagen_2024-08-26_125510479.png',
      pizzine: 'https://imgix.cosmicjs.com/4c3a05c0-7187-11f0-a97c-19f0968aeae2-La_Pizzine.jpg0', // Not used in template, consider removing.
      tuesdayImage: 'https://imgix.cosmicjs.com/3670aed0-d4ce-11ee-9ce5-59949019255e-Vagalume-1.jpg',
      wednesdayImage: 'https://imgix.cosmicjs.com/0801b090-da63-11ee-b6bc-0f88b0862d1c-ilios-10.jpg',
      thursdayImage: 'https://imgix.cosmicjs.com/959b1280-d82d-11ee-9ce5-59949019255e-Bagatelle-9.jpg',
      fridayImage: 'https://imgix.cosmicjs.com/2c95d360-6eb0-11ee-8960-f1f803b242c2-Gitano-4.jpg',
      saturdayImage: 'https://imgix.cosmicjs.com/827cb160-b345-11ef-bee4-3bb1d3c55332-club-papaya-playa-foto.jpg',
      sundayImage: 'https://imgix.cosmicjs.com/3e6b9220-d4cf-11ee-9ce5-59949019255e-vagalume-10.jpg'
    };
  }
};
</script>

<style scoped>
/* Using a similar style structure to the other nightlife pages for consistency */
.nightlife-guide-container {
  background-color: #1a1a1c;
  color: #f0f0f0;
  font-family: Arial, sans-serif;
}

.hero-section {
  height: 70vh;
  min-height: 500px;
  background-size: cover;
  background-position: center;
  position: relative;
}

.hero-overlay {
  background: linear-gradient(to top, rgba(26, 26, 28, 1) 5%, rgba(26, 26, 28, 0.4) 100%);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  text-align: center;
  padding: 3rem 1.5rem;
}

.hero-headline {
  font-family: 'Oswald', sans-serif;
  font-size: 4rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 10px rgba(0, 0, 0, 0.8);
  max-width: 900px;
  line-height: 1.1;
  margin: 0;
}

.hero-subheadline {
  font-size: 1.2rem;
  color: #ccc;
  margin-top: 1rem;
}

.guide-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.intro-text {
  font-size: 0.9rem;
  line-height: 1.8;
  color: #ddd;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem auto;
}

.evocative-quote {
  font-size: 1.25rem;
  font-style: italic;
  text-align: center;
  color: #73854a;
  margin: 2rem 0;
}

.content-section {
  margin-bottom: 3.5rem;
}

.content-section h2 {
  font-size: 2.2rem;
  font-weight: 700;
  color: #73854a;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
}

.content-section .bullet {
  margin-right: 0.75rem;
}

.content-section p, .content-section ul {
  font-size: 0.9rem;
  line-height: 1.8;
  color: #ddd;
}

.content-section strong {
  color: #fff;
  font-weight: 700;
}

.highlight-quote {
  border-left: 4px solid #73854a;
  padding-left: 1.5rem;
  margin: 2rem 0;
  font-style: italic;
  font-size: 0.9rem;
  color: #ccc;
}

.pro-tip {
  background-color: rgba(115, 133, 74, 0.1);
  border: 1px solid #73854a;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-top: 2rem;
}

.gradient-card {
  background: linear-gradient(135deg, #252528, #1f1f21);
}

.card-content {
  flex: 1;
}

.section-image {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.venue-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.venue-card {
  background-color: #2b2b2d;
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  flex-direction: column;
  align-items: stretch;
}

.venue-card-text {
  padding: 1rem;
}

.venue-card h3 {
  margin: 0 0 0.5rem 0;
  color: #73854a;
}

.venue-card p {
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

/*.experience-list {*/
/* display: flex;*/
/* justify-content: space-around;*/
/* text-align: center;*/
/* gap: 1.5rem;*/
/* margin-top: 2rem;*/
/*}*/

/*.experience-item {*/
/* display: flex;*/
/* flex-direction: column;*/
/* align-items: center;*/
/* gap: 1rem;*/
/*}*/

/*.experience-item .responsive-image-container {*/
/* width: 150px;*/
/* height: 150px;*/
/* border-radius: 50%;*/
/* overflow: hidden;*/
/* border: 3px solid #73854a;*/
/*}*/

/*.experience-item span {*/
/* font-weight: bold;*/
/* font-size: 1.1rem;*/
/*}*/

.dos-donts-section {
  margin-top: 4rem;
}

.dos-donts-header {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 2rem;
}

.dos-donts-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.dos-donts-columns .card {
  flex-direction: column;
  align-items: flex-start;
}

.dos-donts-columns h3 {
  font-size: 1.5rem;
  color: #fff;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.dos-donts-columns .emoji {
  font-size: 1.8rem;
  margin-right: 0.5rem;
}

.dos-donts-columns ul {
  list-style: none;
  padding: 0;
}

.dos-donts-columns li {
  margin-bottom: 1rem;
  line-height: 1.7;
}

.gradient-card-dos {
  background: linear-gradient(135deg, rgba(115, 133, 74, 0.1), #1f1f21);
  border-left: 4px solid #73854a;
}

.gradient-card-donts {
  background: linear-gradient(135deg, rgba(255, 64, 129, 0.1), #1f1f21);
  border-left: 4px solid #ff4081;
}

.well {
  background-color: rgba(115, 133, 74, 0.1);
  border: 1px solid #73854a;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-top: 1rem;
}

.weekly-rhythm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.day-card {
  background-color: #2b2b2d;
  border-radius: 8px;
  overflow: hidden;
}

.day-card-content {
  padding: 1.5rem;
}

.day-card h3 {
  font-size: 1.5rem;
  color: #73854a;
  margin: 0 0 1rem 0;
}

.day-card p {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #ccc;
}

.day-card a {
  color: #81d4fa;
  text-decoration: none;
  font-weight: bold;
}

.day-card a:hover {
  text-decoration: underline;
}

@media (max-width: 992px) {
  .guide-content .row .col-lg-8 {
    width: 100%;
  }

  .guide-content .row .d-none {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .hero-headline {
    font-size: 2.8rem;
  }

  .dos-donts-columns, .card {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .section-image {
    width: 100%;
    height: 200px;
  }
}
</style>
